import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Dispatch, bindActionCreators } from "redux";
import { setUserAction, setUserDataAction } from "../loginReducer";
import { SmallLogoWithDashboard } from "../logo/SmallLogo";
import LogoutWidget from "../logout/LogoutWidget";
import { UserData } from "../model/user";
import { ApplicationState } from "../reducers";
import NotificationOverlay from "../status/NotificationOverlay";
import { CenteredRow } from "../layout/Row";
import { ColumnCentered } from "../layout/Column";
import { ClockDriftData, getApproximatedClockDrift, getAvailableRounds, getDevicesInRound } from "../services/rawAudioAnalysisService";
import LoadingAnimation from "../animation/LoadingAnimation";
import EstimatedClockDriftChart from "../plot/estimatedClockDriftChart";
import BorderButton from "../button/BorderButton";

export enum RawDataAnalyticsGraphType {
    NORMAL = 0,
    ACCUMULATED = 1,
}

interface SessionDataForDevice {
    data: { [session: string]: ClockDriftData };
}

interface StateToProps {
    jwtToken: string | undefined;
    user: firebase.User | undefined;
    userData: UserData | undefined;
}

interface DispatchFromProps {
    setUser: (_: firebase.User | undefined) => void;
    setUserData: (_: UserData | undefined) => void;
}

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {
            setUser: setUserAction,
            setUserData: setUserDataAction,
        },
        dispatch
    );

const mapStateToProps = (state: ApplicationState) => ({
    jwtToken: state.login.jwtToken,
    user: state.login.user,
    userData: state.login.userData,
});

interface Props extends StateToProps, DispatchFromProps { }


const SelectRoundDropdown = ({ rounds, selectedRound, setSelectedRound }: { rounds: string[], selectedRound: string, setSelectedRound: (round: string) => void }) => {
    return (
        <div>
            <select
                name="orderBy"
                id="orderBy"
                className={"Widget-dropdown"}
                style={{
                    width: "100%",
                    marginLeft: "0rem",
                    fontSize: "1.25rem",
                }}
                value={selectedRound}
                onChange={(e) => {
                    setSelectedRound(e.target.value);
                }}
            >
                {rounds.map((m) => (
                    <option value={m}>{m}</option>
                ))}
            </select>
        </div>
    )
}


const RoundClockDriftStatistics = ({
    selectedRound,
    jwtToken,
    graphType,
}: {
    selectedRound: string;
    jwtToken: string;
    graphType: RawDataAnalyticsGraphType;
}) => {
    const [deviceData, setDeviceData] = useState<{ [deviceId: string]: SessionDataForDevice }>({});

    useEffect(() => {
        let isCancelled = false;

        const fetchData = async () => {
            try {
                const data = await getDevicesInRound(jwtToken, selectedRound);

                for (const device of data.devices) {
                    try {
                        const driftData = await getApproximatedClockDrift(jwtToken, device.deviceId, selectedRound);
                        if (!isCancelled) {
                            setDeviceData(prev => ({
                                ...prev,
                                [device.deviceId]: { data: driftData },
                            }));
                        }
                    } catch (error) {
                        console.error("Error fetching clock drift for device:", device.deviceId, error);
                    }
                }
            } catch (error) {
                console.error("Error fetching devices in round:", error);
            }
        };

        if (jwtToken && selectedRound) {
            fetchData();
        }

        return () => {
            isCancelled = true;
        };
    }, [jwtToken, selectedRound]);

    if (!deviceData || Object.keys(deviceData).length === 0) {
        return (
            <CenteredRow>
                <LoadingAnimation height={40} width={40} color="blue" />
            </CenteredRow>
        );
    }
    return (
        <div>
            {Object.entries(deviceData).map(([deviceId, data]) => (
                <div key={deviceId}>
                    <DeviceClockDriftStatistics
                        key={deviceId}
                        deviceId={deviceId}
                        data={data.data}
                        graphType={graphType}
                    />
                </div>
            ))}
        </div>
    );
};

const DeviceClockDriftStatistics = ({ deviceId, data, graphType }: { deviceId: string, data: { [key: string]: ClockDriftData }, graphType: RawDataAnalyticsGraphType }) => {
    const [excludedSessions, setExcludedSessions] = useState<string[]>([]);
    const filteredData = useMemo(() => {
        if (excludedSessions.length === 0) {
            return data;
        }
        return Object.fromEntries(
            Object.entries(data).filter(([session]) => !excludedSessions.includes(session))
        );
    }, [data, excludedSessions]);
    const sessions = Object.keys(data);
    const indexedSessions = useMemo(() => {
        return sessions.map((session, index) => ({
            session,
            index,
        }));
    }, [sessions]);
    return (
        (
            <div style={{ marginBottom: "2rem" }}>
                <ColumnCentered>
                    <h2 style={{ marginBottom: "1rem" }}>Device: {deviceId}</h2>
                    <CenteredRow>
                        {sessions.map((session) => (
                            <BorderButton
                                key={session}
                                text={session.slice(0, 10)}
                                onClick={() => {
                                    setExcludedSessions((prev) =>
                                        prev.includes(session)
                                            ? prev.filter((s) => s !== session)
                                            : [...prev, session]
                                    );
                                }}
                                selected={!excludedSessions.includes(session)}
                            />
                        ))}
                    </CenteredRow>
                    <EstimatedClockDriftChart
                        graphData={filteredData}
                        graphType={graphType}
                        indexedSessions={indexedSessions}
                    />
                </ColumnCentered>
            </div>
        )
    );
}

function GraphTypeSelection({ setGraphType, graphType }: { setGraphType; graphType: RawDataAnalyticsGraphType }) {
    return (
        <CenteredRow style={{ paddingBottom: "2.5rem" }}>
            <BorderButton
                key={RawDataAnalyticsGraphType.NORMAL}
                text="Deviation per minute"
                onClick={() => {
                    setGraphType(RawDataAnalyticsGraphType.NORMAL);
                }}
                selected={graphType === RawDataAnalyticsGraphType.NORMAL}
            />
            <BorderButton
                key={RawDataAnalyticsGraphType.ACCUMULATED}
                text="Accumulated deviation"
                onClick={() => {
                    setGraphType(RawDataAnalyticsGraphType.ACCUMULATED);
                }}
                selected={graphType === RawDataAnalyticsGraphType.ACCUMULATED}
            />
        </CenteredRow>
    );
}

const RawDataAnalyticsPage = (props: Props) => {
    const [availableRounds, setAvailableRounds] = useState<string[]>();
    const [selectedRound, setSelectedRound] = useState<string>();
    const [graphType, setGraphType] = useState<RawDataAnalyticsGraphType>(RawDataAnalyticsGraphType.ACCUMULATED);
    const fetchAvailableRounds = () => {
        if (props.jwtToken) {
            getAvailableRounds(props.jwtToken)
                .then((data) => {
                    setAvailableRounds(data.rounds);
                    if (data.rounds.length > 0) {
                        setSelectedRound(data.rounds[0]);
                    }
                })
                .catch((error) => {
                    console.error("Error fetching available rounds:", error);
                });
        }
    }
    useEffect(() => {
        fetchAvailableRounds();
    }, []);

    return (
        <div className="Dashboard">
            <div className={"Dashboard-header"}>
                <SmallLogoWithDashboard fontSizeRem={1.625} />
                <LogoutWidget {...props} />
            </div>
            <NotificationOverlay />
            <CenteredRow style={{ paddingBottom: "2.5rem" }}>
                <h1>Estimated clock drift</h1>
            </CenteredRow>
            <GraphTypeSelection
                setGraphType={setGraphType}
                graphType={graphType}
            />
            {props.jwtToken && selectedRound && availableRounds ? (
                <div>
                    <ColumnCentered>
                        <SelectRoundDropdown
                            rounds={availableRounds}
                            selectedRound={selectedRound}
                            setSelectedRound={setSelectedRound}
                        />
                    </ColumnCentered>
                    <RoundClockDriftStatistics selectedRound={selectedRound} jwtToken={props.jwtToken} graphType={graphType} />
                </div>
            ) : (
                <div></div>
            )}
        </div>
    );
}

export default connect<StateToProps, DispatchFromProps>(
    // @ts-ignore
    mapStateToProps,
    mapDispatchToProps
)(RawDataAnalyticsPage);